import {
  Drawer,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export type NoteDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  note: any;
};

export default function NoteDetailsModal({
  open,
  onClose,
  note,
}: NoteDetailsModalProps) {
  if (!note) return null;

  const renderValue = (value: any) => {
    if (!value) return 'N/A';
    if (typeof value === 'string') return value;
    if (value.display) return value.display;
    return String(value);
  };

  const descriptionText = renderValue(note.description || note.text);

  const fields = [
    { label: 'Topic', value: note.topic },
    { label: 'Author', value: note.author },
    { label: 'Type', value: note.type },
    { label: 'Date', value: note.date },
    { label: 'Medium', value: note.medium },
    { label: 'Description', value: descriptionText, multiline: true },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 420, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Note Details
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {/* Information section */}
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ mb: 2 }}
        >
          Information
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {fields.map(({ label, value, multiline }) => (
            <Box
              key={label}
              sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              {multiline ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {String(value)
                    .split('\n')
                    .map((line) => line.trim())
                    .filter((line) => line.length > 0)
                    .map((line, idx) => (
                      <Typography key={idx} variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.7 }}>
                        {line}
                      </Typography>
                    ))}
                </Box>
              ) : (
                <Typography variant="body2">{renderValue(value)}</Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
}